import { useCallback, useEffect, useRef, useState } from 'react';
import { flushAttendanceQueue, enqueueAttendance, getQueueSize } from '../services/offlineAttendanceQueue';
import { submitAttendance } from '../services/googleScriptServiceCORS';

export function useOfflineSync({ todayAttendance, showToast, setInlineStatus, onSyncSuccess = null }) {
  const [offlineQueueCount, setOfflineQueueCount] = useState(0);
  const [isSyncingOffline, setIsSyncingOffline] = useState(false);
  const isSyncingRef = useRef(false);

  const syncOfflineQueue = useCallback(
    async ({ silent = false } = {}) => {
      if (isSyncingRef.current) {
        return { sent: 0, failed: getQueueSize(), dropped: 0, skipped: true };
      }
      if (!navigator.onLine) {
        setOfflineQueueCount(getQueueSize());
        return { sent: 0, failed: getQueueSize(), dropped: 0, skipped: true };
      }

      isSyncingRef.current = true;
      setIsSyncingOffline(true);

      try {
        const result = await flushAttendanceQueue(async (payload) => submitAttendance(payload));
        setOfflineQueueCount(getQueueSize());

        if (result.sent > 0 && !silent) {
          const msg = `Đã đồng bộ ${result.sent} bản ghi offline`;
          showToast(msg, 'success');
          setInlineStatus({ type: 'success', message: msg });
        }

        if (result.dropped > 0) {
          const msg = `Đã bỏ ${result.dropped} bản ghi offline không còn hợp lệ.`;
          if (!silent) showToast(msg, 'warning');
          setInlineStatus({ type: 'warning', message: msg });
        }

        if ((result.sent > 0 || result.dropped > 0) && typeof onSyncSuccess === 'function') {
          await onSyncSuccess();
        }

        return result;
      } finally {
        isSyncingRef.current = false;
        setIsSyncingOffline(false);
      }
    },
    [onSyncSuccess, setInlineStatus, showToast]
  );

  useEffect(() => {
    setOfflineQueueCount(getQueueSize());
    if (navigator.onLine) {
      syncOfflineQueue({ silent: true }).catch(() => {});
    }
  }, [syncOfflineQueue]);

  useEffect(() => {
    const flushOnOnline = async () => {
      await syncOfflineQueue();
    };

    window.addEventListener('online', flushOnOnline);
    return () => window.removeEventListener('online', flushOnOnline);
  }, [syncOfflineQueue]);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      if (navigator.onLine && getQueueSize() > 0) {
        syncOfflineQueue({ silent: true }).catch(() => {});
      }
    }, 30000);

    return () => window.clearInterval(timerId);
  }, [syncOfflineQueue]);

  useEffect(() => {
    if (!todayAttendance?.checkin_time || todayAttendance?.checkout_time) return undefined;
    if (!('Notification' in window)) return undefined;

    if (Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }

    if (Notification.permission !== 'granted') return undefined;

    const now = new Date();
    const reminderTime = new Date();
    reminderTime.setHours(16, 45, 0, 0);
    const delay = reminderTime.getTime() - now.getTime();

    if (delay <= 0) return undefined;

    const timerId = window.setTimeout(() => {
      new Notification('Nhắc checkout', { body: 'Bạn đã checkin, vui lòng checkout cuối ngày.' });
    }, delay);

    return () => window.clearTimeout(timerId);
  }, [todayAttendance]);

  const enqueueOfflineIfNeeded = async ({ payload, image, error }) => {
    const isNetworkIssue = !navigator.onLine || /network|fetch|failed|timeout/i.test(String(error?.message || ''));
    if (!isNetworkIssue || !image || !payload?.latitude || !payload?.longitude) {
      return false;
    }

    await enqueueAttendance(payload, image);
    const queuedCount = getQueueSize();
    setOfflineQueueCount(queuedCount);

    const msg = `Không có mạng. Đã lưu tạm ${queuedCount} bản ghi, sẽ tự động đồng bộ khi online.`;
    setInlineStatus({ type: 'warning', message: msg });
    showToast('Đã lưu tạm chấm công offline.', 'warning');

    return true;
  };

  return {
    offlineQueueCount,
    isSyncingOffline,
    flushOfflineQueue: syncOfflineQueue,
    enqueueOfflineIfNeeded,
  };
}
